import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { HubConnectionBuilder } from "@microsoft/signalr";

const SignalRContext = createContext();

export const SignalRProvider = ({ children }) => {
    const [connection, setConnection] = useState(null);
    const [connectionId, setConnectionId] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    // Store global event handlers for different components
    const [globalEventHandlers, setGlobalEventHandlers] = useState({
        onComplete: new Map(),
        onComponents: new Map(),
    });

    // Register a global event handler that persists across navigation
    const registerGlobalHandler = useCallback((eventName, handlerId, handler) => {
        console.log(`🔧 SignalR: Registering ${eventName} handler for ${handlerId}`);
        setGlobalEventHandlers((prev) => {
            const newHandlers = new Map(prev[eventName]);
            newHandlers.set(handlerId, handler);
            const result = {
                ...prev,
                [eventName]: newHandlers,
            };
            console.log(`📊 SignalR: ${eventName} handlers count: ${newHandlers.size}`);
            return result;
        });
    }, []);

    // Unregister a global event handler
    const unregisterGlobalHandler = useCallback((eventName, handlerId) => {
        console.log(`🔧 SignalR: Unregistering ${eventName} handler for ${handlerId}`);
        setGlobalEventHandlers((prev) => {
            const newHandlers = new Map(prev[eventName]);
            newHandlers.delete(handlerId);
            const result = {
                ...prev,
                [eventName]: newHandlers,
            };
            console.log(`📊 SignalR: ${eventName} handlers count: ${newHandlers.size}`);
            return result;
        });
    }, []);

    // Helper function to determine workitem type from message content
    // Helper function to determine workitem type from message content or workitem ID
    const determineWorkitemType = useCallback((message) => {
        if (typeof message === "string") {
            // First try to extract workitem ID from the path
            const workitemMatch = message.match(/Workitems\/([^\/]+)/);
            if (workitemMatch) {
                const workitemId = workitemMatch[1];
                if (workitemId.startsWith("hydrovault_")) return "hydrovault";
                if (workitemId.startsWith("eco_")) return "ecoProtector";
                if (workitemId.startsWith("tornado_")) return "tornadoGPTOffline";
            }

            // Fallback to path pattern matching (for backward compatibility)
            if (message.includes("TornadoGPTOfflineStdModel")) return "tornadoGPTOffline";
            if (message.includes("StdModel")) return "ecoProtector";
        }
        return "unknown";
    }, []);

    // Enhanced store pending message with workitem type categorization
    const storePendingMessage = useCallback(
        (eventName, data) => {
            const timestamp = Date.now();
            const workitemType = determineWorkitemType(data);

            // Use type-specific storage keys
            const storageKey = `pendingSignalRMessages_${workitemType}`;
            const pendingMessages = JSON.parse(localStorage.getItem(storageKey) || "{}");

            if (!pendingMessages[eventName]) {
                pendingMessages[eventName] = [];
            }

            const messageData = {
                data,
                timestamp,
                workitemType,
                id: Math.random().toString(36).substr(2, 9),
            };

            pendingMessages[eventName].push(messageData);
            localStorage.setItem(storageKey, JSON.stringify(pendingMessages));

            console.log(`💾 SignalR: Stored pending ${eventName} message for ${workitemType}:`, {
                messageId: messageData.id,
                workitemType,
                timestamp: new Date(timestamp).toISOString(),
                data: typeof data === "string" ? data.substring(0, 100) + "..." : data,
            });

            // Keep only last 10 messages per event to prevent localStorage bloat
            if (pendingMessages[eventName].length > 10) {
                pendingMessages[eventName] = pendingMessages[eventName].slice(-10);
                localStorage.setItem(storageKey, JSON.stringify(pendingMessages));
            }
        },
        [determineWorkitemType]
    );

    // Enhanced get pending messages with workitem type filtering
    const getPendingMessages = useCallback((eventName, workitemType = "unknown") => {
        const storageKey = `pendingSignalRMessages_${workitemType}`;
        const pendingMessages = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const messages = pendingMessages[eventName] || [];

        console.log(
            `📥 SignalR: Retrieved ${messages.length} pending ${eventName} messages for ${workitemType}:`,
            messages.map((m) => ({
                id: m.id,
                workitemType: m.workitemType,
                timestamp: new Date(m.timestamp).toISOString(),
                ageMinutes: Math.round((Date.now() - m.timestamp) / 60000),
            }))
        );

        // Clear the messages after retrieving them (preserving your existing pattern)
        delete pendingMessages[eventName];
        localStorage.setItem(storageKey, JSON.stringify(pendingMessages));

        return messages;
    }, []);

    // Get pending messages without clearing them (for debugging)
    const peekPendingMessages = useCallback((eventName, workitemType = "unknown") => {
        const storageKey = `pendingSignalRMessages_${workitemType}`;
        const pendingMessages = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const messages = pendingMessages[eventName] || [];
        console.log(
            `👀 SignalR: Peeking at ${messages.length} pending ${eventName} messages for ${workitemType}`
        );
        return messages;
    }, []);

    // Helper to get the appropriate workitem type for a handler
    const getHandlerWorkitemType = useCallback((handlerId) => {
        if (handlerId === "hydrovault") return "hydrovault";
        if (handlerId === "ecoProtector") return "ecoProtector";
        if (handlerId === "tornadoGPTOffline") return "tornadoGPTOffline";
        return "unknown";
    }, []);

    useEffect(() => {
        console.log("🚀 SignalR: Initializing connection...");
        const newConnection = new HubConnectionBuilder()
            .withUrl(`${process.env.REACT_APP_API_URL}/api/signalr/designautomation`)
            .withAutomaticReconnect()
            .build();

        const startConnection = async () => {
            try {
                console.log("🔌 SignalR: Starting connection...");
                await newConnection.start();
                const id = await newConnection.invoke("getConnectionId");
                setConnectionId(id);
                setConnection(newConnection);
                setIsConnected(true);
                console.log("✅ SignalR: Connected successfully with ID:", id);
            } catch (err) {
                console.error("❌ SignalR: Failed to connect:", err);
                setIsConnected(false);
            }
        };

        startConnection();

        // Handle reconnection events
        newConnection.onreconnecting(() => {
            console.log("🔄 SignalR: Reconnecting...");
            setIsConnected(false);
        });

        newConnection.onreconnected(() => {
            console.log("🔄 SignalR: Reconnected successfully");
            setIsConnected(true);
        });

        newConnection.onclose(() => {
            console.log("🔌 SignalR: Connection closed");
            setIsConnected(false);
        });

        return () => {
            console.log("🛑 SignalR: Stopping connection...");
            newConnection.stop();
        };
    }, []);

    // Set up global event handlers that persist across navigation
    useEffect(() => {
        if (!connection || !isConnected) {
            console.log("⏳ SignalR: Waiting for connection before setting up handlers...");
            return;
        }

        console.log("🎧 SignalR: Setting up global event handlers...");

        // Handle onComplete events with type-aware routing
        const handleOnComplete = (message) => {
            console.log("📨 SignalR: Global onComplete received:", message);
            const messageWorkitemType = determineWorkitemType(message);
            console.log(
                `📊 SignalR: onComplete handlers count: ${globalEventHandlers.onComplete.size}, message type: ${messageWorkitemType}`
            );

            const handlers = globalEventHandlers.onComplete;
            if (handlers.size === 0) {
                console.log("💾 SignalR: No active onComplete handlers, storing message");
                storePendingMessage("onComplete", message);
                return;
            }

            // Try to find a matching handler for this message type
            let handlerFound = false;
            handlers.forEach((handler, handlerId) => {
                const handlerWorkitemType = getHandlerWorkitemType(handlerId);

                if (handlerWorkitemType === messageWorkitemType) {
                    handlerFound = true;
                    try {
                        console.log(
                            `🎯 SignalR: Calling onComplete handler for ${handlerId} (type match: ${messageWorkitemType})`
                        );
                        handler(message);
                    } catch (error) {
                        console.error(`❌ SignalR: Error in onComplete handler ${handlerId}:`, error);
                    }
                } else {
                    console.log(
                        `⏭️ SignalR: Skipping handler ${handlerId} (type mismatch: handler=${handlerWorkitemType}, message=${messageWorkitemType})`
                    );
                }
            });

            // If no matching handler found, store the message
            if (!handlerFound) {
                console.log("💾 SignalR: No matching handler found, storing message");
                storePendingMessage("onComplete", message);
            }
        };

        // Handle onComponents events with type-aware routing
        const handleOnComponents = (message) => {
            console.log("📨 SignalR: Global onComponents received:", message);
            const messageWorkitemType = determineWorkitemType(message);
            console.log(
                `📊 SignalR: onComponents handlers count: ${globalEventHandlers.onComponents.size}, message type: ${messageWorkitemType}`
            );

            const handlers = globalEventHandlers.onComponents;
            if (handlers.size === 0) {
                console.log("💾 SignalR: No active onComponents handlers, storing message");
                storePendingMessage("onComponents", message);
                return;
            }

            // Try to find a matching handler for this message type
            let handlerFound = false;
            handlers.forEach((handler, handlerId) => {
                const handlerWorkitemType = getHandlerWorkitemType(handlerId);

                if (handlerWorkitemType === messageWorkitemType) {
                    handlerFound = true;
                    try {
                        console.log(
                            `🎯 SignalR: Calling onComponents handler for ${handlerId} (type match: ${messageWorkitemType})`
                        );
                        handler(message);
                    } catch (error) {
                        console.error(`❌ SignalR: Error in onComponents handler ${handlerId}:`, error);
                    }
                } else {
                    console.log(
                        `⏭️ SignalR: Skipping handler ${handlerId} (type mismatch: handler=${handlerWorkitemType}, message=${messageWorkitemType})`
                    );
                }
            });

            // If no matching handler found, store the message
            if (!handlerFound) {
                console.log("💾 SignalR: No matching handler found, storing message");
                storePendingMessage("onComponents", message);
            }
        };

        connection.on("onComplete", handleOnComplete);
        connection.on("onComponents", handleOnComponents);

        console.log("🎧 SignalR: Global event handlers registered");

        return () => {
            console.log("🧹 SignalR: Cleaning up global event handlers...");
            connection.off("onComplete", handleOnComplete);
            connection.off("onComponents", handleOnComponents);
        };
    }, [
        connection,
        isConnected,
        globalEventHandlers,
        storePendingMessage,
        determineWorkitemType,
        getHandlerWorkitemType,
    ]);

    const contextValue = {
        connection,
        connectionId,
        isConnected,
        registerGlobalHandler,
        unregisterGlobalHandler,
        getPendingMessages,
        peekPendingMessages,
        storePendingMessage,
    };

    return <SignalRContext.Provider value={contextValue}>{children}</SignalRContext.Provider>;
};

export const useSignalR = () => {
    const context = useContext(SignalRContext);
    if (!context) {
        throw new Error("useSignalR must be used within a SignalRProvider");
    }
    return context;
};
